// DriverAssignmentSection.tsx
import React from 'react';
import user_icon from '../../../assets/icons/user.svg';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/solid';
import { PiX } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { DriverCandidate, driverCandidatePage } from 'components/Driver/driverCandidates';
import { useRemoteSearch } from 'reusable/RemoteSearch/useRemoteSearch';
import RemoteListFooter from 'reusable/RemoteSearch/RemoteListFooter';

type DriverInterface = DriverCandidate;

interface DriverAssignmentProps {
  selectedDrivers: number[];
  /**
   * EV-493。選んでいる運転者（ID）の名前などの情報。候補（今の検索の結果）に無くても、選んだ一覧に名前を出す。
   * 以前は候補の一覧（全件）から引いており、検索で候補を絞ると、選んだ運転者が選んだ一覧から消えていた。
   */
  knownDrivers: Record<number, DriverInterface>;
  handleDriverSelect: (driver: DriverInterface) => void;
  handleDriverRemove: (driverId: number) => void;
  /** RV-129。保存（作成）を送っている間は運転者の追加・外すをさせない。 */
  disabled?: boolean;
}

const DriverAssignment: React.FC<DriverAssignmentProps> = ({
  selectedDrivers,
  knownDrivers,
  handleDriverSelect,
  handleDriverRemove,
  disabled = false,
}) => {
  const { t } = useTranslation('client');
  // EV-338 と同じ。名前が空の運転者の読み上げは「名前のない運転者」。
  const spokenName = (name?: string | null) => name || t('ui.unnamed-driver', { ns: 'common' });
  // EV-493。候補はサーバーで名前を検索し、20件ずつ読む（以前は全件。編集の画面では検索の文字も効いていなかった）。
  const search = useRemoteSearch<DriverInterface>({
    fetchPage: driverCandidatePage,
    getKey: (driver) => driver.id,
  });
  const drivers = search.items;
  const selected = selectedDrivers.map(
    (id) =>
      knownDrivers[id] ?? { id, name: '', phone: '', profile_picture: null, client_deliveries: [] }
  );

  return (
    <div className="flex my-4 pt-4 px-6">
      <div className="flex w-2/5 flex-col gap-4">
        <h6 className="text-subtitle-1 font-medium">
          {t('detail.driver_assignments')}
        </h6>
        <div className="relative">
          <input
            type="text"
            placeholder={t('manage_client.search_driver')}
            className="block w-full h-10 pl-4 text-caption border border-neutral/50 focus:outline-none focus:ring-neutral/75 focus:border-neutral/75 sm:text-sm rounded-md"
            value={search.query}
            onChange={(e) => search.setQuery(e.target.value)}
            // EV-136。検索欄の Enter で、配送先のフォームを送らない（暗黙の送信で、追加画面では
            // 作成・編集画面では保存されていた）。検索は入力のたびに行う。変換の確定（IME）の
            // Enter は妨げない。
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) e.preventDefault();
            }}
          />
          <MagnifyingGlassIcon className="w-4 h-4 absolute inset-y-3 right-4 text-neutral/75 pointer-events-none" />
        </div>
        <div className="flex flex-col overflow-y-auto h-56 pr-4">
          {drivers.map((driver) => (
              <div
                key={driver.id}
                className="flex flex-row items-center justify-between border-b px-2 py-3"
              >
                <div className="flex flex-row gap-4 items-center">
                  <img
                    src={driver.profile_picture || user_icon}
                    alt=""
                    className="h-10 w-10 object-cover rounded-full"
                  />
                  <div>
                    {/* EV-314。名前・電話番号の空は「-」（EV-311 の表の空と同じ）。 */}
                    <p className="text-body-2 font-medium text-neutral/300">
                      {driver.name || '-'}
                    </p>
                    <p className="text-caption text-neutral/100">
                      {driver.phone || '-'}
                    </p>
                  </div>
                </div>
                {/* A10。運転者は複数の配送先に割り当てられる。ほかの配送先に割り当て済みでも選べる。 */}
                {selectedDrivers.includes(driver.id) ? (
                  <div className="w-8 h-8 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-success" />
                  </div>
                ) : (
                  <button
                    className="w-8 h-8 bg-primary/50 rounded-md flex items-center justify-center"
                    disabled={disabled}
                    onClick={() => handleDriverSelect(driver)}
                    type="button"
                    // 2026-10-24 axe（button-name）。担当ドライバーの割り当てのダイアログと同じ名前。
                    aria-label={t('manage_client.add_driver', { name: spokenName(driver.name) })}
                  >
                    <PlusIcon className="w-4 h-4 text-primary/300" />
                  </button>
                )}
              </div>
            ))}
          <RemoteListFooter search={search} empty={t('ui.no-drivers', { ns: 'client' })} />
        </div>
      </div>
      <div
        className={`flex flex-col w-3/5 ${
          selectedDrivers.length > 0
            ? 'items-start justify-start'
            : 'items-center justify-center'
        }`}
      >
        {selectedDrivers.length > 0 ? (
          <div className="flex flex-col items-start px-4">
            <h6 className="text-subtitle-1 font-medium pb-4">
              {t('ui.total-assignment-count', { count: selectedDrivers.length })}
            </h6>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {selected.map((driver) => (
                  <div
                    key={driver.id}
                    className="relative flex items-center gap-2 p-2 border rounded-md"
                  >
                    <img
                      src={driver.profile_picture || user_icon}
                      alt=""
                      className="w-10 h-10 object-cover rounded-full"
                    />
                    <p className="w-full text-body-2 text-neutral/300 truncate">
                      {driver.name || '-'}
                    </p>
                    <button
                      // フォームの中のボタン。type が無いと「外す」でフォームが送られる。
                      type="button"
                      disabled={disabled}
                      onClick={() => handleDriverRemove(driver.id)}
                      className="text-primary/300"
                      aria-label={t('manage_client.remove_driver', { name: spokenName(driver.name) })}
                    >
                      <PiX size={18} />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 items-center justify-center">
            <img src={user_icon} alt="" className="h-14" />
            <h6 className="text-subtitle-2 font-medium">
              {t('manage_client.assign_driver_title')}
            </h6>
            <p className="text-caption text-center text-neutral/100">
              {t('manage_client.assign_driver_subtitle_1')}
              <br />
              {t('manage_client.assign_driver_subtitle_2')}{' '}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverAssignment;
